/**
 * Returns the full country name for a given ISO country code.
 * Uses Intl.DisplayNames with fallback to the original code or custom country list.
 *
 * @param countryCode - ISO 3166-1 alpha-2 or alpha-3 country code (e.g. "SE", "NO", "DK")
 * @param locale - BCP 47 language tag (defaults to "en")
 * @param countries - Optional list of country objects with code and name for custom overrides
 * @returns Full country name or original country code if unavailable
 */
export const getCountryName = (
  countryCode?: string | null,
  locale = "en",
  countries?: Array<{ code?: string | null; name?: string | null }>,
): string => {
  if (!countryCode || typeof countryCode !== "string") {
    return "";
  }

  const trimmed = countryCode.trim();
  if (!trimmed) {
    return "";
  }

  if (countries?.length) {
    const match = countries.find(
      (c) => c?.code?.toUpperCase() === trimmed.toUpperCase(),
    );
    if (match?.name) {
      return match.name;
    }
  }

  try {
    const regionNames = new Intl.DisplayNames([locale, "en"], {
      type: "region",
    });
    return regionNames.of(trimmed.toUpperCase()) || trimmed;
  } catch {
    return trimmed;
  }
};
